/**
 * The fixed routes whose `<head>` is prerendered at build time (web plan
 * W0.18): `staticHeads.json`, which `scripts/prerender-heads.mjs` writes into
 * `dist/<route>/index.html` and which `<PageHead/>` reads for the same routes, so a
 * crawler that runs JavaScript and one that doesn't see the same head.
 *
 * Pure module (no `window`, no React). The JSON is generated from
 * `buildStaticHeads()` in `./templates`; `staticHeads.test.ts` keeps the two
 * identical.
 */
import data from './staticHeads.json';
import { tFor } from '@/i18n';
import { staticRouteHead, type SeoHead, type StaticHead } from './templates';
import { canonicalFor, splitLocalePath } from './urls';

export const STATIC_HEADS: readonly StaticHead[] = data.routes;

/**
 * The prerendered route a location path belongs to, or null. Matched on the
 * canonical path (lowercase, no trailing slash, query ignored), the same way
 * the SPA's routes and the edge's `try_files` treat it.
 */
export function staticHeadFor(pathname: string): StaticHead | null {
  const path = canonicalFor(pathname.split(/[?#]/, 1)[0]);
  return STATIC_HEADS.find((route) => route.path === path) ?? null;
}

/** `staticHeadFor()` as a complete head for `<PageHead/>`, or null off the fixed routes. */
export function staticSeoHeadFor(pathname: string): SeoHead | null {
  const route = staticHeadFor(pathname);
  if (!route) return null;
  // The route's locale (`/bh/en/brands` → Bahrain's `en-BH`, `og:locale`); an
  // un-prefixed or Pakistan route keeps the default locale's, as before.
  const locale = splitLocalePath(route.path)?.locale;
  return staticRouteHead(route, locale ? { locale, t: tFor(locale) } : {});
}
